import {
  Timeline,
  Typography,
  Tooltip,
  Tag,
  theme,
  Space,
  Select,
  Switch,
  Dropdown,
  Button,
  Avatar,
  Card,
} from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  UserOutlined,
  EditOutlined,
  CommentOutlined,
  BranchesOutlined,
  FileTextOutlined,
  TagOutlined,
  LinkOutlined,
  FilterOutlined,
  FieldTimeOutlined,
  PlayCircleOutlined,
  AppstoreOutlined,
  CloseOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { CSSProperties, useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import { formatDuration } from "src/utils/duration";
import { IntegrationIcon } from "src/components/IntegrationIcon";
import { UserAvatar } from "src/components/UserAvatar";
import { Hover } from "../Hover";
import { MetadataTag, IndicatorType } from "../MetadataTag";
import { CustomTooltip } from "../CustomTooltip";
import React from "react";

dayjs.extend(relativeTime);
dayjs.extend(duration);

const { Text, Link } = Typography;

// Add custom duration formatter
const formatExactDuration = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return `${hours}h ${remainingMinutes}m`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d ${remainingHours}h`;
};

export interface TimelineItem {
  timestamp: string;
  uuid: string;
  action: string;
  integration?: string;
  entityType?: string;
  name?: string;
  actor?: {
    name: string;
    avatarUrl?: string;
  };
  details?: {
    oldValue?: string;
    newValue?: string;
    comment?: string;
    link?: string;
  };
  type: "status" | "comment" | "edit" | "reference" | "other";
  rawEvent: any;
  durationFromPrev?: number | null;
  runningTotal?: number | null;
}

interface EntityTimelineProps {
  history: any[]; // Replace with proper type from your entity history
  className?: string;
  style?: CSSProperties;
}

// Add new types for filter modes
type FilterMode = "include" | "exclude";
type FilterModes = {
  [key: string]: FilterMode;
};

// Add new types for consolidated state
type SelectedFiltersState = {
  [key: string]: string[];
};

const getEventIcon = (type: TimelineItem["type"]) => {
  switch (type) {
    case "status": {
      return <TagOutlined />;
    }
    case "comment": {
      return <CommentOutlined />;
    }
    case "edit": {
      return <EditOutlined />;
    }
    case "reference": {
      return <LinkOutlined />;
    }
    default: {
      return <ClockCircleOutlined />;
    }
  }
};

const getEventColor = (type: TimelineItem["type"]) => {
  switch (type) {
    case "status": {
      return "blue";
    }
    case "comment": {
      return "green";
    }
    case "edit": {
      return "orange";
    }
    case "reference": {
      return "purple";
    }
    default: {
      return "gray";
    }
  }
};

const transformHistoryToTimelineItems = (history: any[]): TimelineItem[] => {
  return history.map((event) => {
    // Determine the event type
    let type: TimelineItem["type"] = "other";
    if (event.newstatus || event.delta?.status) {
      type = "status";
    } else if (event.comment) {
      type = "comment";
    } else if (event.delta) {
      type = "edit";
    } else if (event.reference) {
      type = "reference";
    }

    // Ensure we have a UUID, fallback to a combination of timestamp and type if not present
    const uuid =
      event.uuid || `${event.occurredat}-${type}-${event.action || "updated"}`;

    return {
      timestamp: event.occurredat,
      uuid,
      action: event.action || "updated",
      integration: event.integration,
      entityType: event.entitytype,
      name: event.name,
      actor: event.actor
        ? {
            name: event.actor.name,
            avatarUrl: event.actor.avatarurl,
          }
        : undefined,
      details: {
        oldValue: event.delta?.status?.before,
        newValue: event.newstatus || event.delta?.status?.after,
        comment: event.comment,
        link: event.reference,
      },
      type,
      rawEvent: event,
    };
  });
};

// Add a new type for filter categories
type FilterCategory = {
  key: string;
  label: string;
  icon: React.ReactNode;
  options: { label: string; value: string }[];
};

// Add these helper functions before the EntityTimeline component
const formatValue = (value: any): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value.toString();
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return value.toString();
};

const shouldDisplayKey = (key: string, value: any): boolean => {
  return (
    value !== null &&
    value !== undefined &&
    value !== "" &&
    !["rawEvent", "durationFromPrev"].includes(key)
  );
};

// Add styles at the top of the file after the imports
const styles = `
  .timeline-dot-container:hover .exclude-pill {
    opacity: 1 !important;
  }
`;

export const EntityTimeline: React.FC<EntityTimelineProps> = ({
  history,
  className,
  style,
}): JSX.Element => {
  const { token } = theme.useToken();

  // Consolidate filter states
  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersState>({
    actions: [],
    actors: [],
    integrations: [],
    eventTypes: [],
    entityTypes: [],
    eventNames: [],
    excludedEvents: [],
  });

  const [showDurations, setShowDurations] = useState(true);
  const [showTotalDuration, setShowTotalDuration] = useState(true);
  const [enableSmartGrouping, setEnableSmartGrouping] = useState(true);
  const [filterModes, setFilterModes] = useState<FilterModes>({
    actions: "include",
    actors: "include",
    integrations: "include",
    entityTypes: "include",
    eventTypes: "include",
    eventNames: "include",
  });

  // Create efficient lookups
  const {
    timelineItems,
    uniqueActions,
    uniqueActors,
    uniqueIntegrations,
    uniqueEntityTypes,
    uniqueEventNames,
    itemsByUuid, // Add UUID lookup map
  } = useMemo(() => {
    const items = transformHistoryToTimelineItems(history);

    // Create UUID lookup map
    const itemMap = new Map(items.map((item) => [item.uuid, item]));

    return {
      timelineItems: items,
      uniqueActions: [...new Set(items.map((item) => item.action))],
      uniqueActors: [
        ...new Set(
          items.map((item) => item.actor?.name).filter(Boolean) as string[]
        ),
      ],
      uniqueIntegrations: [
        ...new Set(
          items.map((item) => item.integration).filter(Boolean) as string[]
        ),
      ],
      uniqueEntityTypes: [
        ...new Set(
          items.map((item) => item.entityType).filter(Boolean) as string[]
        ),
      ],
      uniqueEventNames: [
        ...new Set(items.map((item) => item.name).filter(Boolean) as string[]),
      ],
      itemsByUuid: itemMap,
    };
  }, [history]);

  // Optimize groupedEventsMap to include pre-computed grouped items
  const groupedEventsMap = useMemo(() => {
    if (!enableSmartGrouping) return new Map<string, TimelineItem[]>();

    const groups = new Map<string, TimelineItem[]>();
    const processedGroups = new Set<string>();

    timelineItems.forEach((item, index) => {
      if (processedGroups.has(item.uuid)) return;

      const itemTime = dayjs(item.timestamp);
      const baseKey = `${item.actor?.name || ""}-${item.integration || ""}-${
        item.action
      }-${item.rawEvent.entity?.uuid || ""}`;

      // Pre-compute grouped items
      const groupedItems = timelineItems.slice(index + 1).filter((other) => {
        const otherTime = dayjs(other.timestamp);
        const timeDiff = Math.abs(itemTime.diff(otherTime, "minute"));

        return (
          timeDiff <= 5 &&
          `${other.actor?.name || ""}-${other.integration || ""}-${
            other.action
          }-${other.rawEvent.entity?.uuid || ""}` === baseKey
        );
      });

      if (groupedItems.length > 0) {
        groups.set(item.uuid, groupedItems);
        groupedItems.forEach((grouped) => processedGroups.add(grouped.uuid));
      }
    });

    return groups;
  }, [timelineItems, enableSmartGrouping]);

  // Optimize filteredItems to use pre-computed values
  const filteredItems = useMemo(
    () =>
      timelineItems
        .filter((item) => {
          const filters = selectedFilters;
          const actionMatch =
            filters.actions.length === 0 ||
            (filterModes.actions === "include"
              ? filters.actions.includes(item.action)
              : !filters.actions.includes(item.action));
          const actorMatch =
            filters.actors.length === 0 ||
            (filterModes.actors === "include"
              ? item.actor && filters.actors.includes(item.actor.name)
              : !(item.actor && filters.actors.includes(item.actor.name)));
          const integrationMatch =
            filters.integrations.length === 0 ||
            (filterModes.integrations === "include"
              ? item.integration &&
                filters.integrations.includes(item.integration)
              : !(
                  item.integration &&
                  filters.integrations.includes(item.integration)
                ));
          const eventTypeMatch =
            filters.eventTypes.length === 0 ||
            (filterModes.eventTypes === "include"
              ? filters.eventTypes.includes(item.type)
              : !filters.eventTypes.includes(item.type));
          const entityTypeMatch =
            filters.entityTypes.length === 0 ||
            (filterModes.entityTypes === "include"
              ? item.entityType && filters.entityTypes.includes(item.entityType)
              : !(
                  item.entityType &&
                  filters.entityTypes.includes(item.entityType)
                ));
          const eventNameMatch =
            filters.eventNames.length === 0 ||
            (filterModes.eventNames === "include"
              ? item.name && filters.eventNames.includes(item.name)
              : !(item.name && filters.eventNames.includes(item.name)));
          const notExcluded = !filters.excludedEvents.includes(item.uuid);

          const groupingMatch =
            !enableSmartGrouping ||
            ![...groupedEventsMap.values()].some((group) =>
              group.some((grouped) => grouped.uuid === item.uuid)
            );

          return (
            actionMatch &&
            actorMatch &&
            integrationMatch &&
            eventTypeMatch &&
            notExcluded &&
            groupingMatch &&
            entityTypeMatch &&
            eventNameMatch
          );
        })
        .map((item) => {
          const groupedItems = groupedEventsMap.get(item.uuid);
          if (groupedItems) {
            return {
              ...item,
              name: `${item.name} (+${groupedItems.length} similar)`,
              rawEvent: {
                ...item.rawEvent,
                groupedEvents: groupedItems.map((grouped) => grouped.rawEvent),
              },
            };
          }
          return item;
        }),
    [
      timelineItems,
      selectedFilters,
      filterModes,
      enableSmartGrouping,
      groupedEventsMap,
    ]
  );

  // Calculate durations as the final step
  const itemsWithDurations = useMemo(() => {
    let runningTotal = 0;
    const firstEventTime = dayjs(filteredItems.at(-1)?.timestamp); // Get oldest event

    return filteredItems.map((item, index) => {
      const currentTime = dayjs(item.timestamp);

      if (index === 0) {
        // For first (newest) item, calculate running total from first event
        runningTotal = Math.abs(firstEventTime.diff(currentTime));
        return { ...item, durationFromPrev: null, runningTotal };
      }

      const prevItem = filteredItems[index - 1];
      const prevTime = dayjs(prevItem.timestamp);

      // Calculate duration from previous event
      const duration = Math.abs(currentTime.diff(prevTime));

      // Calculate running total from first event
      runningTotal = Math.abs(firstEventTime.diff(currentTime));

      return {
        ...item,
        durationFromPrev: duration,
        runningTotal,
      };
    });
  }, [filteredItems]);

  // Define filter categories
  const filterCategories: FilterCategory[] = useMemo(
    () => [
      {
        key: "eventNames",
        label: "Event Names",
        icon: <FileTextOutlined />,
        options: uniqueEventNames.map((name) => ({ label: name, value: name })),
      },
      {
        key: "actions",
        label: "Actions",
        icon: <FilterOutlined />,
        options: uniqueActions.map((action) => ({
          label: action,
          value: action,
        })),
      },
      {
        key: "actors",
        label: "Actors",
        icon: <UserOutlined />,
        options: uniqueActors.map((actor) => ({ label: actor, value: actor })),
      },
      {
        key: "integrations",
        label: "Integrations",
        icon: <AppstoreOutlined />,
        options: uniqueIntegrations.map((integration) => ({
          label: integration.toLowerCase(),
          value: integration.toLowerCase(),
        })),
      },
      {
        key: "entityTypes",
        label: "Entity Types",
        icon: <BranchesOutlined />,
        options: uniqueEntityTypes.map((type) => ({
          label: type,
          value: type,
        })),
      },
      {
        key: "eventTypes",
        label: "Event Types",
        icon: <TagOutlined />,
        options: [
          { label: "Status Changes", value: "status" },
          { label: "Comments", value: "comment" },
          { label: "Edits", value: "edit" },
          { label: "References", value: "reference" },
        ],
      },
    ],
    [
      uniqueActions,
      uniqueActors,
      uniqueIntegrations,
      uniqueEntityTypes,
      uniqueEventNames,
    ]
  );

  // Update filter handling functions
  const handleFilterChange = useCallback(
    (category: string, values: string[]) => {
      setSelectedFilters((prev) => ({
        ...prev,
        [category]: values,
      }));
    },
    []
  );

  const handleExcludeEvent = useCallback((uuid: string) => {
    setSelectedFilters((prev) => ({
      ...prev,
      excludedEvents: [...prev.excludedEvents, uuid],
    }));
  }, []);

  const removeFilter = useCallback((category: string, value: string) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [category]: prev[category].filter((v) => v !== value),
    }));
  }, []);

  // Create dropdown menu items
  const menuItems = filterCategories.map((category) => ({
    key: category.key,
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Select
          size="small"
          value={filterModes[category.key]}
          style={{ width: 100 }}
          options={[
            { label: "Include", value: "include" },
            { label: "Exclude", value: "exclude" },
          ]}
          onChange={(value: FilterMode) => {
            setFilterModes((prev) => ({ ...prev, [category.key]: value }));
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <Select
          mode="multiple"
          placeholder={`Select ${category.label}`}
          style={{ width: 200 }}
          options={category.options}
          value={selectedFilters[category.key]}
          onChange={(values: string[]) =>
            handleFilterChange(category.key, values)
          }
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    ),
    icon: category.icon,
  }));

  // Update selectedFilters calculation
  const selectedFilterPills = Object.entries(selectedFilters).flatMap(
    ([category, values]) => values.map((value) => ({ category, value }))
  );

  return (
    <>
      <style>{styles}</style>
      <div
        className={className}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          ...style,
        }}
      >
        {/* Fixed header section */}
        <div
          style={{
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            backgroundColor: token.colorBgContainer,
            padding: "16px",
            zIndex: 1,
          }}
        >
          {/* Component header with title and controls */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: selectedFilterPills.length > 0 ? 16 : 0,
            }}
          >
            <Typography.Title
              level={5}
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              Timeline
              <Text
                type="secondary"
                style={{ fontSize: 14, fontWeight: "normal" }}
              >
                ({itemsWithDurations.length} of {timelineItems.length} items)
              </Text>
            </Typography.Title>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <Switch
                checked={enableSmartGrouping}
                onChange={setEnableSmartGrouping}
                checkedChildren={
                  <span>
                    <AppstoreOutlined /> Group Similar
                  </span>
                }
                unCheckedChildren={
                  <span>
                    <AppstoreOutlined /> Show All
                  </span>
                }
                style={{ minWidth: 120 }}
              />
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "duration-settings",
                      type: "group",
                      label: "Time Display",
                      children: [
                        {
                          key: "duration-toggle",
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span>Show Duration</span>
                              <Switch
                                checked={showDurations}
                                onChange={setShowDurations}
                                size="small"
                              />
                            </div>
                          ),
                        },
                        {
                          key: "total-toggle",
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span>Show Running Total</span>
                              <Switch
                                checked={showTotalDuration}
                                onChange={setShowTotalDuration}
                                size="small"
                              />
                            </div>
                          ),
                        },
                      ],
                    },
                    { type: "divider" },
                    ...filterCategories.map((category) => ({
                      key: category.key,
                      icon: category.icon,
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Select
                            size="small"
                            value={filterModes[category.key]}
                            style={{ width: 100 }}
                            options={[
                              { label: "Include", value: "include" },
                              { label: "Exclude", value: "exclude" },
                            ]}
                            onChange={(value: FilterMode) => {
                              setFilterModes((prev) => ({
                                ...prev,
                                [category.key]: value,
                              }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Select
                            mode="multiple"
                            placeholder={`Select ${category.label}`}
                            style={{ width: 200 }}
                            options={category.options}
                            value={selectedFilters[category.key]}
                            onChange={(values: string[]) =>
                              handleFilterChange(category.key, values)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      ),
                    })),
                  ],
                }}
                trigger={["click"]}
              >
                <Button icon={<SettingOutlined />} type="text" size="small">
                  Settings{" "}
                  {selectedFilterPills.length > 0 &&
                    `(${selectedFilterPills.length})`}
                </Button>
              </Dropdown>
            </div>
          </div>

          {/* Filter pills */}
          {selectedFilterPills.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {selectedFilterPills.map(({ category, value }) => {
                const categoryConfig = filterCategories.find(
                  (c) => c.key === category
                );
                const label =
                  category === "excludedEvents"
                    ? timelineItems.find((item) => item.uuid === value)?.name ||
                      `Event ${dayjs(
                        timelineItems.find((item) => item.uuid === value)
                          ?.timestamp
                      ).format("MMM D, YYYY HH:mm:ss")}`
                    : categoryConfig?.options.find((o) => o.value === value)
                        ?.label || value;
                const mode =
                  category === "excludedEvents"
                    ? "exclude"
                    : filterModes[category];
                return (
                  <Tag
                    key={`${category}-${value}`}
                    closable
                    onClose={() => removeFilter(category, value)}
                    style={{ margin: 0 }}
                  >
                    {categoryConfig?.icon} {mode === "exclude" && "Exclude:"}{" "}
                    {label}
                  </Tag>
                );
              })}
            </div>
          )}
        </div>

        {/* Scrollable timeline content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px",
            backgroundColor: token.colorBgContainer,
          }}
        >
          <Timeline
            items={itemsWithDurations.map((item, index) => ({
              dot: (
                <div
                  className="timeline-dot-container"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    position: "relative",
                    cursor: "pointer",
                  }}
                >
                  <div
                    className="exclude-pill"
                    style={{
                      position: "absolute",
                      right: "100%",
                      marginRight: 4,
                      opacity: 0,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <Tag
                      style={{
                        cursor: "pointer",
                        padding: "0 4px",
                        height: 16,
                        lineHeight: "14px",
                        fontSize: "12px",
                        backgroundColor: token.colorBgLayout,
                        border: `1px solid ${token.colorBorder}`,
                        margin: 0,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExcludeEvent(item.uuid);
                      }}
                    >
                      <CloseOutlined style={{ fontSize: "10px" }} />
                    </Tag>
                  </div>
                  {item.actor ? (
                    <Tooltip title={`Actor: ${item.actor.name}`}>
                      <UserAvatar
                        user={{
                          uuid: item.actor.name,
                          name: item.actor.name,
                          avatarUrl: item.actor.avatarUrl,
                        }}
                        size="small"
                        showTooltip
                        shape="circle"
                        showName={false}
                      />
                    </Tooltip>
                  ) : (
                    getEventIcon(item.type)
                  )}
                </div>
              ),
              color: getEventColor(item.type),
              children: (
                <div
                  className="hover-show"
                  style={{ padding: "4px 0", position: "relative" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      position: "relative",
                      paddingRight: 200, // Make room for the duration text
                    }}
                  >
                    {item.integration && (
                      <Tooltip title={`Integration: ${item.integration}`}>
                        <IntegrationIcon
                          source={item.integration}
                          size="small"
                        />
                      </Tooltip>
                    )}
                    {item.name && (
                      <Text
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.name}
                      </Text>
                    )}
                    <Text
                      type="secondary"
                      style={{
                        position: "absolute",
                        right: 0,
                        whiteSpace: "nowrap",
                        color: token.colorTextSecondary,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <span>
                        {showDurations && item.durationFromPrev ? (
                          <Tooltip title="Time since previous event">
                            <span>
                              <FieldTimeOutlined style={{ marginRight: 4 }} />
                              {formatExactDuration(item.durationFromPrev)}
                            </span>
                          </Tooltip>
                        ) : (
                          <>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {dayjs(item.timestamp).fromNow()}
                          </>
                        )}
                      </span>
                      {showTotalDuration && item.runningTotal && (
                        <Tooltip title="Total time from first event">
                          <span
                            style={{
                              borderLeft: `1px solid ${token.colorBorder}`,
                              paddingLeft: 12,
                            }}
                          >
                            <PlayCircleOutlined style={{ marginRight: 4 }} />
                            {formatExactDuration(item.runningTotal)}
                          </span>
                        </Tooltip>
                      )}
                    </Text>
                  </div>

                  {/* New row for metadata tags */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 4,
                      flexWrap: "wrap",
                    }}
                  >
                    {item.entityType && (
                      <Tooltip title="Entity Type">
                        <MetadataTag
                          metadata={item.entityType}
                          type={IndicatorType.entity}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {item.action && (
                      <Tooltip title="Action">
                        <MetadataTag
                          metadata={item.action}
                          type={IndicatorType.action}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              ),
            }))}
          />
        </div>
      </div>
    </>
  );
};
